/**
 * The sampled one-shot layer.
 *
 * Every cue in this game has two possible sources: the synth in `./sfx.ts`,
 * which builds it from oscillators at runtime, and a generated sample from
 * `public/audio/sfx/`. This module is the second one. `./index.ts` prefers a
 * sample when it has loaded and falls back to the synth otherwise, so the game
 * is never silent — a failed fetch, a cold cache or a `404` costs fidelity, not
 * sound.
 *
 * ## Why samples route through `SynthBus` rather than straight to the output
 *
 * The obvious implementation — `new Audio(url).play()` — would have been much
 * shorter and would have quietly thrown away everything the mixer does:
 *
 * - **Panning.** Cues are placed in the stereo field at the pile they belong
 *   to. That is why the assets are mastered to mono; see `tools/generate-audio.mjs`.
 * - **Ducking.** A bonus stack pulls the card layer down so four simultaneous
 *   cues read as one gesture instead of mud.
 * - **The compressor and limiter** at the end of the chain, which is what lets
 *   that stack happen without clipping.
 * - **Voice stealing.** `bus.channel()` registers the source so the pool can
 *   stop it when the cap is hit, instead of letting a fast player accumulate
 *   unbounded overlapping decays.
 *
 * So a sample allocates a channel exactly like a synth voice does, and differs
 * only in what it hangs off it.
 *
 * ## Timing
 *
 * Buffers are decoded once at load. `play()` is scheduled on the AudioContext
 * clock, never on `setTimeout`, so a cue lands where the choreographer asked
 * for it even under a loaded main thread.
 */

import type { Channel, MixGroup, SynthBus } from '@/audio/synth';

/** Cue names that have a generated sample. Mirrors the manifest in `tools/`. */
export type SampleName =
  | 'cardDeal'
  | 'cardLand'
  | 'cardFlip'
  | 'shuffle'
  | 'twentyOne'
  | 'fiveCard'
  | 'wild'
  | 'streak'
  | 'bust'
  | 'tickWarning'
  | 'gameOver'
  | 'perfectGame'
  | 'uiHover'
  | 'uiPress'
  | 'undo';

/** Cue name to file stem under `public/audio/sfx/`. */
const FILES: Record<SampleName, string> = {
  cardDeal: 'card-deal',
  cardLand: 'card-land',
  cardFlip: 'card-flip',
  shuffle: 'shuffle',
  twentyOne: 'twenty-one',
  fiveCard: 'five-card',
  wild: 'wild',
  streak: 'streak',
  bust: 'bust',
  tickWarning: 'tick-warning',
  gameOver: 'game-over',
  perfectGame: 'perfect-game',
  uiHover: 'ui-hover',
  uiPress: 'ui-press',
  undo: 'undo',
};

/**
 * Which mix group each cue belongs to. Cards and UI are the constant,
 * repetitive layer; bonuses are the occasional loud one that ducks the others.
 */
const GROUPS: Record<SampleName, MixGroup> = {
  cardDeal: 'card',
  cardLand: 'card',
  cardFlip: 'card',
  shuffle: 'card',
  twentyOne: 'bonus',
  fiveCard: 'bonus',
  wild: 'bonus',
  streak: 'bonus',
  bust: 'bonus',
  tickWarning: 'ui',
  gameOver: 'bonus',
  perfectGame: 'bonus',
  uiHover: 'ui',
  uiPress: 'ui',
  undo: 'ui',
};

/**
 * Reverb send per cue, 0..1.
 *
 * The assets are mastered dry precisely so this is a decision the game makes
 * rather than one baked into a file. Card handling is close and physical and
 * gets almost none; the celebratory cues are allowed to bloom into the room.
 */
const SENDS: Record<SampleName, number> = {
  cardDeal: 0.05,
  cardLand: 0.08,
  cardFlip: 0.05,
  shuffle: 0.1,
  twentyOne: 0.3,
  fiveCard: 0.34,
  wild: 0.38,
  streak: 0.26,
  bust: 0.18,
  tickWarning: 0.04,
  gameOver: 0.4,
  perfectGame: 0.42,
  uiHover: 0,
  uiPress: 0.02,
  undo: 0.1,
};

/**
 * Per-cue trim, linear.
 *
 * Mastering peak-normalises every asset to the same ceiling, which equalises
 * headroom but not *loudness* — a 3-second fanfare at -1.5dB peak is far louder
 * to the ear than a 0.2s click at the same peak. These are the balance, and
 * they are the numbers to change when a cue feels wrong in the mix.
 */
const TRIMS: Record<SampleName, number> = {
  cardDeal: 0.5,
  cardLand: 0.62,
  cardFlip: 0.5,
  shuffle: 0.6,
  twentyOne: 0.72,
  fiveCard: 0.76,
  wild: 0.78,
  streak: 0.62,
  bust: 0.7,
  tickWarning: 0.34,
  gameOver: 0.8,
  perfectGame: 0.85,
  uiHover: 0.22,
  uiPress: 0.34,
  undo: 0.45,
};

/**
 * The 2x-6x streak ladder, as playback-rate ratios of the single `streak`
 * sample.
 *
 * One sample pitched up beats five separate generations. A ladder's whole job
 * is to be recognisably the same sound climbing, and five independent
 * text-to-sound calls would differ in timbre, attack and length in ways no
 * amount of prompt-wrangling fixes. These are just-intonation intervals —
 * unison, major second, major third, perfect fifth, major sixth — so the climb
 * is musical rather than merely higher.
 */
const STREAK_RATIOS = [1, 9 / 8, 5 / 4, 3 / 2, 5 / 3];

/** Random detune applied to repetitive card cues, in cents either side. */
const CARD_DETUNE_CENTS = 45;

/** Cues that repeat often enough that an identical replay becomes noticeable. */
const VARIED: ReadonlySet<SampleName> = new Set<SampleName>([
  'cardDeal',
  'cardLand',
  'cardFlip',
  'tickWarning',
  'uiHover',
  'uiPress',
]);

export interface SamplePlayOptions {
  /** Context time to start at. Defaults to `bus.now()`. */
  when?: number;
  /** Linear level scale on top of the cue's own trim. */
  level?: number;
  /** -1..1. Omitted leaves the cue centred. */
  pan?: number;
  /** Playback rate multiplier. Also transposes; 1 is as recorded. */
  rate?: number;
}

function cents(amount: number): number {
  return Math.pow(2, amount / 1200);
}

/**
 * Decoded one-shots, and the machinery to play one.
 *
 * Loading is best-effort and non-blocking: `load()` never rejects, and any cue
 * whose fetch or decode failed simply reports `has() === false` forever, which
 * routes it back to the synth.
 */
export class SampleKit {
  readonly #buffers = new Map<SampleName, AudioBuffer>();
  #loading: Promise<void> | null = null;
  #failed = 0;

  /** How many cues are playable from a sample right now. */
  get loaded(): number {
    return this.#buffers.size;
  }

  /** How many cues failed to load and have fallen back to the synth. */
  get failed(): number {
    return this.#failed;
  }

  get complete(): boolean {
    return this.#buffers.size + this.#failed >= Object.keys(FILES).length;
  }

  has(name: SampleName): boolean {
    return this.#buffers.has(name);
  }

  /**
   * Fetch and decode every one-shot. Idempotent — concurrent calls share one
   * pass, and a second call after completion is a no-op.
   *
   * @param ctx - the context to decode against. Buffers are context-agnostic in
   *   practice, but decoding needs one.
   * @param base - URL prefix, so a subdirectory deploy still resolves.
   */
  load(ctx: BaseAudioContext, base = 'audio/sfx/'): Promise<void> {
    this.#loading ??= this.#loadAll(ctx, base);
    return this.#loading;
  }

  async #loadAll(ctx: BaseAudioContext, base: string): Promise<void> {
    const names = Object.keys(FILES) as SampleName[];
    await Promise.all(
      names.map(async (name) => {
        try {
          const res = await fetch(`${base}${FILES[name]}.mp3`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const bytes = await res.arrayBuffer();
          this.#buffers.set(name, await ctx.decodeAudioData(bytes));
        } catch (err) {
          // Deliberately soft: one missing asset must not take the audio layer
          // down with it. The synth still has a voice for every cue.
          this.#failed += 1;
          console.warn(`[audio] sample "${name}" unavailable, using synth:`, err);
        }
      }),
    );
  }

  /**
   * Play a cue.
   *
   * @returns `true` if a sample was scheduled. `false` means the caller should
   *   fall back to the synth — that is the whole contract with `./index.ts`.
   */
  play(bus: SynthBus, name: SampleName, options: SamplePlayOptions = {}): boolean {
    const buffer = this.#buffers.get(name);
    if (!buffer) return false;

    const when = options.when ?? bus.now();
    // Small random detune on the cues that fire most, so a fast player hears
    // variation rather than the same 300ms of audio stamped out repeatedly.
    const variation = VARIED.has(name)
      ? cents((Math.random() * 2 - 1) * CARD_DETUNE_CENTS)
      : 1;
    // `Math.max` propagates NaN rather than rejecting it, so a non-finite rate
    // would reach `playbackRate` — where Web Audio throws — instead of being
    // clamped. Check finiteness explicitly.
    const requested = (options.rate ?? 1) * variation;
    const rate = Number.isFinite(requested) ? Math.max(0.05, requested) : 1;

    const channel: Channel = bus.channel(when, when + buffer.duration / rate + 0.02, {
      group: GROUPS[name],
      send: SENDS[name],
      ...(options.pan === undefined ? {} : { pan: options.pan }),
    });

    const source = bus.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;

    const gain = bus.ctx.createGain();
    gain.gain.value = TRIMS[name] * (options.level ?? 1);

    source.connect(gain).connect(channel.input);
    channel.own(source);
    source.start(when);
    return true;
  }

  /**
   * The streak ladder. `level` is 1..5, i.e. the 2x-6x callouts.
   *
   * @returns `true` if a sample was scheduled.
   */
  playStreak(bus: SynthBus, level: number, options: SamplePlayOptions = {}): boolean {
    // Same NaN trap as in `play()`: an out-of-range index would read
    // `undefined` out of the ratio table and turn the whole rate into NaN.
    const requested = Number.isFinite(level) ? Math.round(level) : 1;
    const index = Math.min(Math.max(requested, 1), STREAK_RATIOS.length) - 1;
    return this.play(bus, 'streak', {
      ...options,
      rate: (options.rate ?? 1) * STREAK_RATIOS[index],
      // The ladder climbs in pitch; let it climb slightly in level too.
      level: (options.level ?? 1) * (1 + index * 0.06),
    });
  }

  dispose(): void {
    this.#buffers.clear();
    this.#loading = null;
    this.#failed = 0;
  }
}
